import { useState } from "react";
import Switch from "react-switch";
import Input from "@semcore/input";
import Button from "@semcore/button";
import { Flex } from "@semcore/flex-box";
import { Text } from "@semcore/typography";
import { Box, FormControlLabel, Typography, Paper } from "@mui/material";
import ColorPicker, { PaletteManager } from "@semcore/color-picker";

import UploadImage from "./UploadImage";
import { validateCustomLink } from "./helper";
import AddProjectStore from "../../../../store/AddProjectStore";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TrashM from "@semcore/ui/icon/Trash/m";
import EditM from "@semcore/ui/icon/Edit/m";
import MathPlusM from "@semcore/ui/icon/MathPlus/m";

const systemColor = [
  "#2C5282",
  "#5A9FDE",
  "#994C8E",
  "#FAA41D",
  "#F15B4D",
  "#137C99",
];

const headerLinks = [
  {
    label: "Buy Guest Posts",
    url: "https://app.rankspro.io/platform/sites",
  },
  {
    label: "Services",
    url: "",
  },
];

const HeaderComponent = () => {
  const email = localStorage.getItem("admin_mail");
  const { white_lable, showNotification } = AddProjectStore((state) => state);
  const [customLink, setCustomLink] = useState({
    id: 1,
    label: "",
    url: "",
  });
  const [error, setError] = useState({
    id: null,
    label: "",
    url: "",
  });

  const setCustomColors = (list) => {
    AddProjectStore.setState((state) => {
      return {
        white_lable: {
          ...state.white_lable,
          theme_color_list: list,
        },
      };
    });
  };

  const resetHeaderLogo = () => {
    AddProjectStore.setState((state) => {
      return {
        white_lable: {
          ...state.white_lable,
          header_logo: null,
        },
      };
    });
  };

  const handleThemeColor = (color) => {
    AddProjectStore.setState((state) => {
      const currentColor = state.white_lable?.theme_color;
      const newColor = currentColor === color ? null : color; // Toggle logic

      return {
        white_lable: {
          ...state.white_lable,
          isUpdate: true,
          theme_color: newColor,
        },
      };
    });
  };

  const handleToggleHeaderSetting = (key) => {
    AddProjectStore.setState((state) => ({
      white_lable: {
        ...state.white_lable,
        isUpdate: true,
        [key]: !state.white_lable?.[key],
      },
    }));
  };

  const handleFileUploadForHeaderLogo = (file) => {
    if (!file) return;
    const MAX_FILE_SIZE = 100 * 1024; // 100 KB
    const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif"];

    const isValidType = ALLOWED_TYPES.includes(file.type);
    const isValidSize = file.size <= MAX_FILE_SIZE;

    if (!isValidType) {
      showNotification({
        message: "❌ Only JPEG, PNG, or GIF images are allowed.",
        severity: "error",
      });
      return resetHeaderLogo();
    }

    if (!isValidSize) {
      showNotification({
        message: "Image size must not exceed 100KB.",
        severity: "error",
      });
      return resetHeaderLogo();
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      // Rename file here 🛠️
      const extension = file.name.split(".").pop();
      const renamedFile = new File([file], `${email}_logo.${extension}`, {
        type: file.type,
      });

      AddProjectStore.setState({
        white_lable: {
          ...white_lable,
          isUpdate: true,
          header_logo: {
            src: reader.result,
            type: renamedFile,
          },
        },
      });
    };
    reader.readAsDataURL(file);
  };

  const handleEditCustomLink = (newLink) => {
    setCustomLink(newLink);
    deleteCustomLink(newLink.id);
  };
  const deleteCustomLink = (linkId) => {
    AddProjectStore.setState((state) => ({
      white_lable: {
        ...state.white_lable,
        isUpdate: true,
        header_custom_links: (
          state.white_lable?.header_custom_links || []
        ).filter((link) => link.id !== linkId),
      },
    }));
  };

  const handleSubmitClick = (newLink) => {
    const isError = validateCustomLink(newLink);
    if (isError.label || isError.url) {
      setError(isError);
    } else {
      AddProjectStore.setState((state) => {
        const existingLinks = state.white_lable?.header_custom_links || [];

        const updatedLinks = existingLinks.some(
          (link) => link.id === newLink.id
        )
          ? existingLinks.map((link) =>
              link.id === newLink.id ? newLink : link
            )
          : [...existingLinks, newLink];

        updatedLinks.sort((a, b) => a.id - b.id);

        return {
          white_lable: {
            ...state.white_lable,
            isUpdate: true,
            header_custom_links: updatedLinks,
          },
        };
      });
      setError({
        id: null,
        label: "",
        url: "",
      });
      setCustomLink({
        id: newLink.id + 1,
        label: "",
        url: "",
      });
    }
  };

  return (
    <Paper
      elevation={3}
      className="p-3"
      sx={{ boxShadow: "none", border: "2px solid #C4C7CF" }}
    >
      <Typography variant="subtitle1" gutterBottom>
        1. Header settings
      </Typography>
      <Flex direction="column" gap={1} mb={3} w={350}>
        <Text tag="label" htmlFor="header-logo" size={200}>
          Header logo:
        </Text>
        <UploadImage
          key={"header"}
          type="header"
          onChange={handleFileUploadForHeaderLogo}
          resetLogo={resetHeaderLogo}
          Logo={white_lable.header_logo}
          note={"Only JPEG, PNG, or GIF formats. Max size: 100KB."}
        />
      </Flex>
      <Flex
        gap={3}
        mb={3}
        w={350}
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Flex direction="column" gap={1}>
          <Text tag="label" htmlFor="header-logo" size={200}>
            Show Header links:
          </Text>
          <FormControlLabel
            value="end"
            control={
              <Switch
                className="react-switch"
                onColor="#6a3be4"
                onChange={() => handleToggleHeaderSetting("show_header_links")}
                checked={white_lable.show_header_links || false}
                aria-labelledby="neat-label"
                uncheckedIcon={false}
                checkedIcon={false}
                width={40}
                height={20}
              />
            }
            label={white_lable.show_header_links ? "On" : "Off"}
            labelPlacement="end"
            sx={{
              margin: 0,
              "& .MuiTypography-root": {
                marginLeft: "10px",
              },
            }}
          />
        </Flex>
        <Flex direction="column" gap={1}>
          <Text tag="label" htmlFor="header-logo" size={200}>
            Use custom links:
          </Text>
          <FormControlLabel
            value="end"
            control={
              <Switch
                className="react-switch"
                onColor="#6a3be4"
                onChange={() =>
                  handleToggleHeaderSetting("show_header_custom_links")
                }
                checked={white_lable.show_header_custom_links || false}
                aria-labelledby="neat-label"
                uncheckedIcon={false}
                checkedIcon={false}
                width={40}
                height={20}
              />
            }
            label={white_lable.show_header_custom_links ? "On" : "Off"}
            labelPlacement="end"
            sx={{
              margin: 0,
              "& .MuiTypography-root": {
                marginLeft: "10px",
              },
            }}
          />
        </Flex>
      </Flex>
      {white_lable.show_header_custom_links ? (
        <Box>
          {white_lable.header_custom_links?.map((linkItem, index) => {
            return (
              <Flex
                gap={3}
                mb={3}
                alignItems="flex-start"
                key={`custom-link-${index}`}
              >
                <Flex direction="column" gap={1} w={200}>
                  <Text tag="label" size={200}>
                    Text shown for the link:
                  </Text>

                  <Input h={"35px"} disabled>
                    <Input.Value
                      defaultValue={linkItem.label}
                      placeholder=""
                      type="text"
                      autoComplete="off"
                    />
                  </Input>
                </Flex>
                <Flex direction="column" gap={1} w={400}>
                  <Flex gap={2} justifyContent="space-between">
                    <Text tag="label" size={200} display="flex">
                      Where should the link go:
                    </Text>
                    {linkItem?.url && (
                      <a href={linkItem?.url} target="_blank">
                        <OpenInNewIcon
                          sx={{
                            width: "18px",
                            height: "18px",
                            color: "#6a3be4",
                          }}
                        />
                      </a>
                    )}
                  </Flex>
                  <Input h={"35px"} disabled>
                    <Input.Value
                      defaultValue={linkItem.url}
                      placeholder=""
                      type="text"
                      autoComplete="off"
                    />
                  </Input>
                </Flex>
                <Box className="mt-auto">
                  <Button
                    title="edit"
                    h={34}
                    w={34}
                    onClick={() => handleEditCustomLink(linkItem)}
                  >
                    <Button.Addon>
                      <EditM />
                    </Button.Addon>
                  </Button>
                  <Button
                    title="remove"
                    ml={2}
                    h={34}
                    w={34}
                    onClick={() => deleteCustomLink(linkItem.id)}
                  >
                    <Button.Addon>
                      <TrashM />
                    </Button.Addon>
                  </Button>
                </Box>
              </Flex>
            );
          })}

          {white_lable.header_custom_links?.length < 2 && (
            <Flex gap={3} mb={3} alignItems="flex-start">
              <Flex direction="column" gap={1} w={200}>
                <Text tag="label" size={200}>
                  Text shown for the link:
                </Text>

                <Input h={"35px"} state={error?.label ? "invalid" : "normal"}>
                  <Input.Value
                    value={customLink.label}
                    placeholder=""
                    type="text"
                    autoComplete="off"
                    onChange={(value) => {
                      setCustomLink((pre) => ({
                        ...pre,
                        label: value,
                      }));
                    }}
                  />
                </Input>
                {error.label && error.id === customLink.id ? (
                  <Text size={100} color="#ff4953">
                    {error.label}
                  </Text>
                ) : null}
              </Flex>
              <Flex direction="column" gap={1} w={400}>
                <Flex gap={2} justifyContent="space-between">
                  <Text tag="label" size={200} display="flex">
                    Where should the link go:
                  </Text>
                </Flex>
                <Flex gap={2} justifyContent="space-between">
                  <Input h={"35px"} state={error?.url ? "invalid" : "normal"}>
                    <Input.Value
                      value={customLink.url}
                      placeholder=""
                      type="url"
                      autoComplete="off"
                      onChange={(value) => {
                        setCustomLink((pre) => ({ ...pre, url: value }));
                      }}
                    />
                  </Input>
                  {customLink.label && customLink.url && (
                    <Button
                      title="add"
                      h={34}
                      w={34}
                      onClick={() => handleSubmitClick(customLink)}
                    >
                      <Button.Addon>
                        <MathPlusM />
                      </Button.Addon>
                    </Button>
                  )}
                </Flex>
                {error?.url && error.id === customLink.id ? (
                  <Text size={100} color="#ff4953">
                    {error.url}
                  </Text>
                ) : null}
              </Flex>
            </Flex>
          )}
        </Box>
      ) : white_lable.show_header_links ? (
        <Box className="mb-4">
          {headerLinks?.map((linkItem) => {
            return (
              <Flex gap={3} mb={3} alignItems="center" key={linkItem.label}>
                <Flex direction="column" gap={1} w={200}>
                  <Text tag="label" size={200}>
                    Text shown for the link:
                  </Text>

                  <Input h={"35px"} disabled>
                    <Input.Value
                      value={linkItem.label}
                      placeholder=""
                      type="text"
                      autoComplete="off"
                    />
                  </Input>
                </Flex>
                <Flex direction="column" gap={1} w={400}>
                  <Flex gap={2} justifyContent="space-between">
                    <Text tag="label" size={200} display="flex">
                      Where should the link go:
                    </Text>
                    {linkItem?.url && (
                      <a href={linkItem?.url} target="_blank">
                        <OpenInNewIcon
                          sx={{
                            width: "18px",
                            height: "18px",
                            color: "#6a3be4",
                          }}
                        />
                      </a>
                    )}
                  </Flex>
                  <Input h={"35px"} disabled>
                    <Input.Value
                      value={linkItem.url}
                      placeholder=""
                      type="text"
                      autoComplete="off"
                    />
                  </Input>
                </Flex>
              </Flex>
            );
          })}
        </Box>
      ) : null}
      <Typography variant="subtitle1" gutterBottom>
        UI Color scheme
      </Typography>
      <Flex flexWrap gap={5} mb={3}>
        <Flex direction="column" gap={1}>
          <Text tag="label" size={200}>
            System colors:
          </Text>
          <ul className="d-flex align-items-center ui-scheme-white-lable">
            {systemColor?.map((color) => {
              return (
                <li
                  className="sys-col"
                  key={color}
                  style={{
                    backgroundColor: color,
                    outline:
                      white_lable.theme_color === color
                        ? "2px solid rgb(106, 59, 228)"
                        : "none",
                    border:
                      white_lable.theme_color === color
                        ? "2px solid #d6d8e3"
                        : "none",
                  }}
                  onClick={() => handleThemeColor(color)}
                ></li>
              );
            })}
          </ul>
        </Flex>

        <Flex direction="column" gap={1} className="white-lable-color-picker">
          <Text tag="label" size={200}>
            Custom Color:
          </Text>
          <ColorPicker
            value={white_lable.theme_color || "#2c5282"}
            onChange={handleThemeColor}
          >
            <ColorPicker.Trigger id="player-1-color" className="color-picker" />
            <ColorPicker.Popper>
              <ColorPicker.Colors
                colors={[
                  "#8649E1",
                  "#FF5733",
                  "#98848D",
                  "#8E3B29",
                  "#B0E727",
                  "#27D3E7",
                  "#2D747C",
                  "#6ad0de",
                  "#6E2D7C",
                ]}
              />
              <PaletteManager
                colors={white_lable.theme_color_list}
                onColorsChange={setCustomColors}
              >
                <PaletteManager.Colors />
                <PaletteManager.InputColor />
              </PaletteManager>
            </ColorPicker.Popper>
          </ColorPicker>
        </Flex>
      </Flex>
      <Flex direction="column" gap={1} mb={3}>
        <Text tag="label" size={200}>
          Show Upgrade:
        </Text>
        <FormControlLabel
          value="end"
          control={
            <Switch
              className="react-switch"
              onColor="#6a3be4"
              onChange={() => handleToggleHeaderSetting("show_upgrade")}
              checked={white_lable.show_upgrade}
              aria-labelledby="neat-label"
              uncheckedIcon={false}
              checkedIcon={false}
              width={40}
              height={20}
            />
          }
          label={white_lable.show_upgrade ? "On" : "Off"}
          labelPlacement="end"
          sx={{
            margin: 0,
            "& .MuiTypography-root": {
              marginLeft: "10px",
            },
          }}
        />
      </Flex>
      <Flex direction="column" gap={1}>
        <Text tag="label" size={200}>
          Show Translation:
        </Text>
        <FormControlLabel
          value="end"
          control={
            <Switch
              className="react-switch"
              onColor="#6a3be4"
              onChange={() => handleToggleHeaderSetting("show_translation")}
              checked={white_lable.show_translation}
              aria-labelledby="neat-label"
              uncheckedIcon={false}
              checkedIcon={false}
              width={40}
              height={20}
            />
          }
          label={white_lable.show_translation ? "On" : "Off"}
          labelPlacement="end"
          sx={{
            margin: 0,
            "& .MuiTypography-root": {
              marginLeft: "10px",
            },
          }}
        />
      </Flex>
    </Paper>
  );
};

export default HeaderComponent;
